<script setup>
import { ref, onMounted } from "vue";
import { apiQuery } from "@/api/j24api";
import { useRouter, useRoute } from "vue-router";

const router = useRouter();
const route = useRoute();
const licenseId = ref("");

const loading = ref(false);
const acceptedCheckbox = ref(false);
const error = ref("");

onMounted(() => {
  // Get licenseId from query parameter
  const queryLicenseId = route.query.licenseId;
  licenseId.value = typeof queryLicenseId === "string" ? queryLicenseId : "";

  if (!licenseId.value) {
    error.value = "No license ID provided";
  }
});

async function acceptEula() {
  if (!acceptedCheckbox.value) {
    error.value = "You must accept the EULA to continue";
    return;
  }

  if (!licenseId.value) {
    error.value = "No license ID provided";
    return;
  }

  loading.value = true;
  error.value = "";

  try {
    await apiQuery("ELA", "patching/patchingManagement", {
      action: "acceptEula",
      licenseId: licenseId.value,
    });

    // Navigate to dashboard with refresh flag
    router.push({
      name: "PatchingV2Dashboard",
      query: { eulaAccepted: "true" },
    });
  } catch (err) {
    console.error("Error accepting EULA:", err);
    error.value = "Failed to accept EULA. Please try again.";
  }

  loading.value = false;
}

function decline() {
  router.push({ name: "HomeView" });
}
</script>

<template>
  <v-container>
    <v-row justify="center">
      <v-col cols="12" md="10" lg="8">
        <v-card>
          <v-card-title class="text-h4 pa-6">
            End User License Agreement
          </v-card-title>

          <v-card-text>
            <v-sheet
              class="pa-4 mb-4"
              style="max-height: 500px; overflow-y: auto"
              border
              rounded
            >
              <h3 class="mb-3">
                End User Licensing Agreement (EULA) – Patch24
              </h3>

              <h4 class="mb-3 mt-4">Definitions</h4>

              <v-table density="compact" class="mb-4">
                <tbody>
                  <tr>
                    <td
                      style="width: 30%; vertical-align: top"
                      class="font-weight-bold"
                    >
                      Agreement
                    </td>
                    <td>
                      The End User Licensing Agreement (EULA) between the
                      Licensee (you) and Jigsaw24 (Licensor) governing the use
                      of the Patch24 software.
                    </td>
                  </tr>
                  <tr>
                    <td
                      style="width: 30%; vertical-align: top"
                      class="font-weight-bold"
                    >
                      Licensor
                    </td>
                    <td>
                      Jigsaw24, the company granting the licence to use Patch24.
                    </td>
                  </tr>
                  <tr>
                    <td
                      style="width: 30%; vertical-align: top"
                      class="font-weight-bold"
                    >
                      Licensee
                    </td>
                    <td>
                      The individual or entity who is granted the right to use
                      Patch24 under this Agreement.
                    </td>
                  </tr>
                  <tr>
                    <td
                      style="width: 30%; vertical-align: top"
                      class="font-weight-bold"
                    >
                      Software
                    </td>
                    <td>
                      Patch24, including the local agent installed on macOS
                      devices and the management portal.
                    </td>
                  </tr>
                  <tr>
                    <td
                      style="width: 30%; vertical-align: top"
                      class="font-weight-bold"
                    >
                      Device
                    </td>
                    <td>
                      Any macOS hardware on which the Patch24 software is
                      installed or used.
                    </td>
                  </tr>
                  <tr>
                    <td
                      style="width: 30%; vertical-align: top"
                      class="font-weight-bold"
                    >
                      Licence Bundle
                    </td>
                    <td>
                      A predefined set of licences sold together, each covering
                      the installation of Patch24 on a single device.
                    </td>
                  </tr>
                  <tr>
                    <td
                      style="width: 30%; vertical-align: top"
                      class="font-weight-bold"
                    >
                      Standard Business Hours
                    </td>
                    <td>
                      Refers to the period during which the Supplier provides
                      regular support and service availability.
                    </td>
                  </tr>
                  <tr>
                    <td
                      style="width: 30%; vertical-align: top"
                      class="font-weight-bold"
                    >
                      Service Level
                    </td>
                    <td>
                      Refers to a defined and measurable standard of performance
                      applicable to the Services provided by the Licensor.
                    </td>
                  </tr>
                  <tr>
                    <td
                      style="width: 30%; vertical-align: top"
                      class="font-weight-bold"
                    >
                      Heartbeat Data
                    </td>
                    <td>
                      An electronic signal sent from devices with Patch24
                      installed, used to monitor how many devices have the
                      software for licence compliance purposes.
                    </td>
                  </tr>
                  <tr>
                    <td
                      style="width: 30%; vertical-align: top"
                      class="font-weight-bold"
                    >
                      Approved Applications
                    </td>
                    <td>
                      macOS applications that have been tested and approved by
                      Jigsaw24 for patch management through Patch24.
                    </td>
                  </tr>
                  <tr>
                    <td
                      style="width: 30%; vertical-align: top"
                      class="font-weight-bold"
                    >
                      Smoke Testing
                    </td>
                    <td>
                      A preliminary test conducted by Jigsaw24 to verify the
                      basic functionality and stability of updates to Approved
                      Applications. This aims to identify critical failures that
                      would prevent the application from launching or performing
                      its core functions, but does not include full regression
                      or compatibility testing.
                    </td>
                  </tr>
                  <tr>
                    <td
                      style="width: 30%; vertical-align: top"
                      class="font-weight-bold"
                    >
                      Application Catalogue
                    </td>
                    <td>
                      The list of macOS applications currently supported and
                      approved for patch management by Patch24.
                    </td>
                  </tr>
                  <tr>
                    <td
                      style="width: 30%; vertical-align: top"
                      class="font-weight-bold"
                    >
                      Update
                    </td>
                    <td>
                      Any bug fix, patch, error correction, or change to the
                      Software provided by Jigsaw24.
                    </td>
                  </tr>
                  <tr>
                    <td
                      style="width: 30%; vertical-align: top"
                      class="font-weight-bold"
                    >
                      Support Services
                    </td>
                    <td>
                      Technical assistance, usage guidance, bug fixes, and
                      patches provided by Jigsaw24 as described in the EULA and
                      on the product documentation site.
                    </td>
                  </tr>
                  <tr>
                    <td
                      style="width: 30%; vertical-align: top"
                      class="font-weight-bold"
                    >
                      Data Hosting
                    </td>
                    <td>
                      The storage of data relevant to Patch24 services within
                      data centres located in the United Kingdom and the
                      European Union.
                    </td>
                  </tr>
                  <tr>
                    <td
                      style="width: 30%; vertical-align: top"
                      class="font-weight-bold"
                    >
                      Data Protection and Compliance
                    </td>
                    <td>
                      Jigsaw24's commitment to comply with applicable data
                      protection laws, including GDPR, for data hosted within
                      the EU.
                    </td>
                  </tr>
                  <tr>
                    <td
                      style="width: 30%; vertical-align: top"
                      class="font-weight-bold"
                    >
                      Confidential Information
                    </td>
                    <td>
                      All information marked or designated as confidential, or
                      that should be considered confidential due to its nature,
                      obtained in connection with this Agreement.
                    </td>
                  </tr>
                  <tr>
                    <td
                      style="width: 30%; vertical-align: top"
                      class="font-weight-bold"
                    >
                      Term
                    </td>
                    <td>
                      The period during which the Licensee is entitled to use
                      Patch24, beginning upon installation or acceptance and
                      continuing for the purchased duration, with automatic
                      renewal unless terminated as described in the EULA.
                    </td>
                  </tr>
                  <tr>
                    <td
                      style="width: 30%; vertical-align: top"
                      class="font-weight-bold"
                    >
                      Professional Services Agreement
                    </td>
                    <td>
                      A separate agreement that may be required for continuous
                      or expert support beyond standard product assistance,
                      potentially subject to additional fees.
                    </td>
                  </tr>
                </tbody>
              </v-table>

              <h4 class="mb-2 mt-4">Introduction</h4>
              <p class="mb-3">
                This End User Licensing Agreement ("Agreement") is a legal
                agreement between you ("Licensee") and Jigsaw24 ("Licensor") for
                the use of the Patch24 software, including the local agent
                installed on macOS devices and the management portal
                ("Software"). By installing, accessing, or using the Software,
                you agree to be bound by the terms of this Agreement.
              </p>

              <h4 class="mb-2 mt-4">Grant of License</h4>
              <p class="mb-3">
                Jigsaw24 grants you a revocable, non-exclusive,
                non-transferable, limited license to use the Patch24 in
                accordance with the terms of this Agreement. You do not acquire
                any ownership rights in Patch24, including all intellectual
                property rights therein (such as copyrights, patents,
                trademarks, and trade secrets), will remain the sole and
                exclusive property of Jigsaw24.
              </p>

              <h4 class="mb-2 mt-4">Licensing Allocations & Scope</h4>
              <p class="mb-3">
                The Software is licensed on a per-device basis. You must obtain
                a valid licence for each device on which the Software is
                installed or used. Licences are sold in predefined bundles, and
                any use beyond the number of devices covered by your current
                bundle will require the purchase of additional licence bundles.
                It is your responsibility to ensure that your usage remains
                within the scope of your licensed entitlement.
              </p>
              <p class="mb-3">
                Jigsaw24 gathers heartbeat data from devices with the Software
                installed for the purposes of monitoring usage against license
                agreements.
              </p>

              <h4 class="mb-2 mt-4">Use of Software</h4>
              <p class="mb-3">
                You are authorised to use the Software for the purposes of
                application patch management, hardware and software inventory
                review, patch status verification, update initiation, policy
                setting for updates, and monitoring overall estate patch version
                details.
              </p>

              <h4 class="mb-2 mt-4">Restrictions</h4>
              <p class="mb-2">You shall not:</p>
              <ul class="mb-3 ml-10">
                <li>
                  Modify, adapt, translate, reverse engineer, decompile, or
                  disassemble the Software.
                </li>
                <li>
                  Rent, lease, loan, resell, sublicense, or distribute the
                  Software.
                </li>
                <li>Use the Software to perform any illegal activities.</li>
              </ul>

              <h4 class="mb-2 mt-4">Support Services</h4>
              <p class="mb-3">
                Jigsaw24 will provide you with support services, including
                technical assistance, usage guidance, bug fixes, and patches,
                for the duration of the Term, in accordance with the Service
                Levels documented on Jigsaw24's Product documentation site. As a
                condition of receiving support, you must first consult the
                documentation before logging an issue.
              </p>
              <p class="mb-3">
                <a href="https://docs.jigsaw24.com/Patch24/" target="_blank"
                  >https://docs.jigsaw24.com/Patch24/</a
                >
              </p>
              <p class="mb-3">
                If the issue remains unresolved after reasonable efforts to
                self-diagnose using the documentation, you may then submit a
                support request via Jigsaw24's online support portal.
              </p>
              <p class="mb-3">
                If you require continuous or ongoing support beyond standard
                product assistance, verified not to be bug or issue, or if you
                need expert guidance in applying the documented procedures,
                Jigsaw24 reserves the right to offer such services under a
                separate professional services agreement, which may be subject
                to additional fees.
              </p>

              <h4 class="mb-2 mt-4">Updates Patch 24 Agent</h4>
              <p class="mb-3">
                Jigsaw24 may from time to time in its sole discretion develop
                and provide bug fixes, patches error corrections and/or changes
                to the version of the Software ("Updates"). Updates may modify
                or delete certain features and functionality. You agree that
                Jigsaw24 has no obligation to provide any Updates or to continue
                to provide or enable any features or functionality. However,
                should Jigsaw24 provide you with an Update you agree to download
                and install such Update as and when released, at any time during
                the Term of this Agreement, this will be facilitated by Patch24
                and treated as a standard application. Failure to install any
                Update(s) may render the support services null and void. Further
                information relating to supported versions can be found on our
                documentation site.
              </p>

              <h4 class="mb-2 mt-4">Approved Application Patches</h4>
              <p class="mb-3">
                Jigsaw24 shall monitor vendor releases and conduct preliminary
                testing (smoke testing) to verify the basic functionality and
                stability of applicable updates to Approved Applications in a
                controlled test environment before making them available through
                the Service. Smoke testing aims to identify critical failures
                that would prevent the application from launching or performing
                its core functions but does not include full regression or
                compatibility testing.
              </p>
              <p class="mb-3">
                The Customer acknowledges and agrees that Jigsaw24 shall not be
                liable for any operational issues, data loss, or
                incompatibilities that may arise in the Customer's environment
                following the application of vendor-provided updates, including
                but not limited to issues affecting the Customer's application
                estate. It is the Customer's sole responsibility to perform
                further application functionality testing within their
                environment, at their discretion, to ensure compatibility and
                stability.
              </p>

              <h4 class="mb-2 mt-4">Application Catalogue Requests</h4>
              <p class="mb-3">
                Customers may request the inclusion of additional macOS
                applications not currently listed in the Approved Applications
                catalogue. All such requests shall be subject to assessment of
                the technical feasibility, security implications, and
                compatibility with the Service. A policy relating to software
                additions to the catalogue is outlined on the product
                documentation site.
              </p>
              <p class="mb-3">
                Jigsaw24 shall make reasonable efforts to review and respond to
                such requests within business hours in accordance with its
                standard support service levels. Jigsaw24 makes no guarantee
                that any requested application will be approved or added.
              </p>

              <h4 class="mb-2 mt-4">Data Usage</h4>
              <p class="mb-3">
                The Software collects information regarding installed
                applications, patching status, and hardware inventory for the
                purpose of providing Software functionality.
              </p>

              <h4 class="mb-2 mt-4">Data Hosting</h4>
              <p class="mb-3">
                Jigsaw24 shall host the portal and data relevant to the services
                outlined in this Agreement within datacentres located in the
                United Kingdom and the European Union.
              </p>

              <h4 class="mb-2 mt-4">Data Protection and Compliance</h4>
              <p class="mb-3">
                Jigsaw24 is committed to complying with all applicable data
                protection laws and regulations, including the General Data
                Protection Regulation (GDPR) for data hosted within the European
                Union. By using the Software, you acknowledge and agree that
                your data may be processed and stored in these locations.
              </p>

              <h4 class="mb-2 mt-4">Data Security</h4>
              <p class="mb-3">
                Jigsaw24 shall implement appropriate technical and
                organisational measures to ensure the security of the data
                hosted within the UK and EU datacentres, protecting it against
                unauthorised access, loss, or destruction.
              </p>

              <h4 class="mb-2 mt-4">Data Transfer</h4>
              <p class="mb-3">
                Jigsaw24 shall not transfer your data outside the European
                Economic Area (EEA) without your prior consent and without
                ensuring that adequate safeguards are in place to protect the
                data in compliance with applicable data protection laws.
              </p>

              <h4 class="mb-2 mt-4">Term and Termination</h4>
              <p class="mb-3">
                The term of this Agreement will begin when you download and
                install or otherwise begin using the Software and/or otherwise
                acknowledge your acceptance and will continue in full force and
                effect for the time-limited duration for which you have
                purchased a licence right to use ("Term"). Thereafter the Term
                shall renew automatically for further periods of twelve (12)
                months at the then current list price unless terminated by
                either Party giving the other Party not less than thirty (30)
                days prior written notice. Such notice to take effect at the end
                of the initial time-limited duration or the then current renewal
                period as applicable.
              </p>
              <p class="mb-3">
                Jigsaw24 may, at its discretion, terminate or suspend this
                Agreement and your licence, access, or use of all or any portion
                of the Software with reasonable notice, including where Jigsaw24
                determines that continued access would pose a risk to compliance
                with applicable laws or to the security of the Software, data,
                or services. Jigsaw24 is entitled to seek injunctive relief if
                your use of the Software violates any restrictions set forth in
                this Agreement or applicable law. This Agreement will terminate
                automatically at the end of the specified duration or earlier if
                you fail to comply with any of its terms and conditions. In the
                event that a vendor software update, bug, or software change
                renders the Software unsupportable, Jigsaw24 will provide you
                with no less than 30 working days' notice before terminating
                this Agreement. Upon termination, you must cease all use of the
                Software and destroy all copies in your possession.
              </p>

              <h4 class="mb-2 mt-4">Suspension for Non-Payment</h4>
              <p class="mb-3">
                Jigsaw24 reserves the right to suspend your access to the
                Software, management portal, and related services, either
                temporarily or permanently, if you fail to pay any fees due
                under this Agreement. Access may be restored upon receipt of all
                outstanding payments, subject to Jigsaw24's discretion.
              </p>

              <h4 class="mb-2 mt-4">Refunds</h4>
              <p class="mb-3">
                Except as required by applicable law, all fees paid for the
                Software are non-refundable once the Software has been activated
                or downloaded. No refunds will be issued after activation or
                download, regardless of usage.
              </p>

              <h4 class="mb-2 mt-4">Severability</h4>
              <p class="mb-3">
                If any part, term or provision of this Licence, not being of a
                fundamental nature, is held to be illegal or unenforceable, the
                validity or enforceability of the remainder of this Licence
                shall not be affected.
              </p>

              <h4 class="mb-2 mt-4">Changes to this Agreement</h4>
              <p class="mb-3">
                Jigsaw24 reserves the right, at its sole discretion, to modify
                or replace this Agreement at any time. If a revision is material
                we will provide at least 30 days' notice prior to any new terms
                taking effect. What constitutes a material change will be
                determined at the sole discretion of Jigsaw24. By continuing to
                access or use the Application after any revisions become
                effective, You agree to be bound by the revised terms. If You do
                not agree to the new terms, You are no longer authorized to use
                the Application.
              </p>

              <h4 class="mb-2 mt-4">Governing Law</h4>
              <p class="mb-3">
                This Agreement shall be governed by and construed in accordance
                with the laws of England and Wales.
              </p>

              <h4 class="mb-2 mt-4">DISCLAIMER OF WARRANTIES</h4>
              <p class="mb-3">
                THE SOFTWARE IS PROVIDED "AS IS" WITHOUT WARRANTY OF ANY KIND,
                EITHER EXPRESS OR IMPLIED, INCLUDING, BUT NOT LIMITED TO, THE
                IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A
                PARTICULAR PURPOSE. COMPANY DOES NOT WARRANT THAT THE SOFTWARE
                WILL MEET YOUR REQUIREMENTS OR THAT THE OPERATION OF THE
                SOFTWARE WILL BE UNINTERRUPTED OR ERROR-FREE.
              </p>

              <h4 class="mb-2 mt-4">LIMITATION OF LIABILITY</h4>
              <p class="mb-3">
                BOTH PARTIES ACCEPT UNLIMITED LIABILITY FOR: DEATH OR PERSONAL
                INJURY CAUSED BY ITS GROSS NEGLIGENCE; FRAUD OR FRAUDULENT
                MISREPRESENTATION; OR ANY OTHER ACT OR OMISSION FOR WHICH
                LIABILITY CANNOT BE LIMITED BY LAW. COMPANY SHALL NOT BE LIABLE
                FOR ANY COSTS, EXPENSES, DAMAGES AND LOSSES SUFFERED OR INCURRED
                BY YOU ARISING OUT OF OR IN CONNECTION WITH ANY FRAUDULENT,
                NEGLIGENT OR UNAUTHORISED USE OF THE SOFTWARE OR SERVICE BY YOU,
                YOUR OFFICERS, AGENTS OR EMPLOYEES OR ANY THIRD PARTY ACTING ON
                YOUR BEHALF. SUBJECT TO THE ABOVE, JIGSAW24'S LIABILITY IN
                RESPECT OF ANY CLAIMS, DEMANDS, DAMAGES, COSTS (INCLUDING LEGAL
                COSTS) AND EXPENSES RESULTING FROM ANY TORTIOUS ACT OR OMISSION
                AND/OR BREACH OF THESE TERMS AND CONDITIONS SHALL NOT EXCEED AN
                AMOUNT EQUAL TO 125% OF THE TOTAL AMOUNT PAID OR PAYABLE IN
                RESPECT OF THE SOFTWARE LICENCE IN THE TWELVE (12) MONTHS
                PRECEDING THE CLAIM. COMPANY SHALL NOT BE LIABLE FOR ANY
                ADDITIONAL OR ASSOCIATED COSTS INCURRED IN OPERATING THE
                SOFTWARE, INCLUDING BUT NOT LIMITED TO HARDWARE, SOFTWARE, CLOUD
                SERVICES, SIEM INTEGRATIONS, OR DATA STORAGE. COMPANY SHALL NOT
                BE LIABLE FOR THE LOSS OF ANY ANTICIPATED SAVINGS, PROFITS OR
                SALES EXPECTED OUT OF THE USE OF THE SOFTWARE BY YOU. COMPANY
                SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, OR
                CONSEQUENTIAL DAMAGES, INCLUDING, BUT NOT LIMITED TO, DAMAGES
                FOR LOSS OF PROFITS, LOSS OF DATA, OR BUSINESS INTERRUPTION,
                ARISING OUT OF THE USE OF OR INABILITY TO USE THE SOFTWARE, EVEN
                IF COMPANY HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
                JIGSAW24 SHALL NOT BE LIABLE FOR ANY DAMAGES OR CLAIMS RESULTING
                FROM MALICIOUS OR ACCIDENTAL MISUSE OF THE SOFTWARE.
              </p>

              <h4 class="mb-2 mt-4">INDEMNITY</h4>
              <p class="mb-3">
                You will, to the fullest extent permitted by law, indemnify
                Jigsaw24, and its officers, directors, shareholders, employees
                and agents and their respective successors and assigns
                (collectively, the "Indemnified Parties") against and hold the
                Indemnified Parties harmless from any and all claims, losses,
                liabilities, damages, judgments, awards, settlements, costs and
                expenses, including reasonable legal fees in connection with
                investigating, defending or settling any claim relating to or
                arising out of any breach by you of this Agreement, your
                implementation or use of the Software in a way that infringes or
                misappropriates any third party's Intellectual Property Rights
                or violates applicable law or any acts or omissions on the part
                of you which gives rise to claims against the Indemnified
                Parties.
              </p>

              <h4 class="mb-2 mt-4">EXPORT RESTRICTIONS</h4>
              <p class="mb-3">
                You acknowledge that the Software may be subject to export
                control laws and regulations. You will not, directly or
                indirectly, export, re-export or release the Software or make
                the Software accessible from any jurisdiction or country to
                which export, re-export or release is prohibited by law or
                regulation.
              </p>

              <h4 class="mb-2 mt-4">CONFIDENTIALITY</h4>
              <p class="mb-3">
                For the purpose of this clause "Confidential Information" means
                all information which is marked or designated as confidential or
                should otherwise be considered confidential due to its nature.
                You acknowledge that you may obtain Confidential Information
                belonging to Jigsaw24 in connection with this Agreement. You
                shall not access or use, or permit the access or use of any
                Company Confidential Information other than is necessary to
                perform or exercise your rights hereunder. You may not knowingly
                disclose, or permit to be disclosed, Company Confidential
                Information to any third party without Jigsaw24's prior written
                consent, except that you may disclose the Confidential
                Information solely to your employees, officers, directors,
                consultants, contractors, agents or advisors who have a need to
                know strictly for the purpose of fulfilling your obligations
                under this Agreement. The foregoing will not apply to any
                information that: (i) was or becomes generally known by the
                public; (ii) was known to you without restriction on disclosure,
                prior to disclosure; (iii) was lawfully disclosed by a third
                party to you without restriction; or (iv) is expressly permitted
                to be disclosed pursuant to the terms of this Agreement.
              </p>

              <h4 class="mb-2 mt-4">PRIVACY</h4>
              <p class="mb-3">
                Information collected by Jigsaw24 about you is generally
                governed by Jigsaw24's privacy policy, which can be viewed at
                <a
                  href="https://www.jigsaw24.com/privacy-notice"
                  target="_blank"
                  >https://www.jigsaw24.com/privacy-notice</a
                >.
              </p>

              <h4 class="mb-2 mt-4">GOVERNING LAW</h4>
              <p class="mb-3">
                This Agreement will be governed by and construed in accordance
                with the laws of England and Wales and the Parties hereby submit
                to the jurisdiction of the English courts.
              </p>

              <h4 class="mb-2 mt-4">ENTIRE AGREEMENT</h4>
              <p class="mb-3">
                This Agreement constitutes the entire agreement between you and
                Company with respect to the Software and supersedes all prior or
                contemporaneous communications and proposals, whether oral or
                written, between you and Company regarding the Software.
              </p>

              <h4 class="mb-2 mt-4">ASSIGNMENT</h4>
              <p class="mb-3">
                You may not assign this Agreement or otherwise transfer any
                rights or obligations under this Agreement, without the prior
                written consent of Jigsaw24.
              </p>

              <h4 class="mb-2 mt-4">SEVERABILITY</h4>
              <p class="mb-3">
                If any provision of this Agreement is held to be invalid or
                unenforceable, the remaining provisions will remain in full
                force and effect.
              </p>

              <h4 class="mb-2 mt-4">ACKNOWLEDGEMENT</h4>
              <p class="mb-3">
                BY INSTALLING, COPYING, OR OTHERWISE USING THE SOFTWARE, YOU
                ACKNOWLEDGE THAT YOU HAVE READ AND ACCEPT THE TERMS OF THIS
                AGREEMENT.
              </p>

              <p class="mt-4"><strong>Last Updated:</strong> November 2025</p>
            </v-sheet>

            <v-alert v-if="error" type="error" class="mb-4">
              {{ error }}
            </v-alert>

            <v-checkbox v-model="acceptedCheckbox" color="primary" hide-details>
              <template v-slot:label>
                <span class="text-body-1">
                  I have read and agree to the terms of this End User License
                  Agreement
                </span>
              </template>
            </v-checkbox>
          </v-card-text>

          <v-card-actions class="pa-6">
            <v-spacer></v-spacer>
            <v-btn
              variant="elevated"
              color="success"
              @click="acceptEula"
              :loading="loading"
              :disabled="!acceptedCheckbox"
            >
              Accept and Continue
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>
